import { Input } from "@/components/ui/input";
import { RotateCcw } from "lucide-react";
import { COMUNI, ZONE, TIPOLOGIE } from "@/data/properties";

const selectCls =
  "w-full h-11 rounded-xl border border-border-subtle bg-white px-3 text-sm outline-none focus:border-brand";
const inputCls =
  "h-11 rounded-xl border-border-subtle bg-white focus-visible:ring-brand/30 focus-visible:border-brand";

function Toggle({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3.5 py-2 text-sm font-medium border transition-all ${
        active ? "bg-brand text-white border-brand" : "bg-white text-anthracite/70 border-border-subtle hover:border-brand"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

const MinSelect = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-anthracite/60">{label}</span>
    <select className={`${selectCls} mt-1`} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}>
      <option value={0}>Indifferente</option>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>{n}+</option>
      ))}
    </select>
  </label>
);

export default function Filters({ filters, setFilters, onReset, resultCount }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div className="bg-white rounded-3xl border border-border-subtle p-5 sm:p-6 space-y-5" data-testid="filters-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold text-anthracite">Filtra</h2>
        <button onClick={onReset} className="inline-flex items-center gap-1.5 text-sm text-anthracite/60 hover:text-brand transition-colors" data-testid="filters-reset">
          <RotateCcw className="h-3.5 w-3.5" /> Azzera
        </button>
      </div>

      {/* transazione */}
      <div className="flex gap-2">
        {["tutti", "vendita", "affitto"].map((t) => (
          <button
            key={t}
            onClick={() => set({ transazione: t })}
            className={`flex-1 rounded-xl py-2.5 text-sm font-semibold capitalize transition-all ${
              filters.transazione === t ? "bg-brand text-white" : "bg-accent-subtle text-anthracite/70"
            }`}
            data-testid={`filter-trans-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-semibold text-anthracite/60">Comune</span>
          <select className={`${selectCls} mt-1`} value={filters.comune} onChange={(e) => set({ comune: e.target.value })} data-testid="filter-comune">
            <option value="">Tutti</option>
            {COMUNI.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-anthracite/60">Zona</span>
          <select className={`${selectCls} mt-1`} value={filters.zona} onChange={(e) => set({ zona: e.target.value })} data-testid="filter-zona">
            <option value="">Tutte</option>
            {ZONE.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-semibold text-anthracite/60">Tipologia</span>
        <select className={`${selectCls} mt-1`} value={filters.tipologia} onChange={(e) => set({ tipologia: e.target.value })} data-testid="filter-tipologia">
          <option value="">Tutte</option>
          {TIPOLOGIE.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>

      <div>
        <span className="text-xs font-semibold text-anthracite/60">Prezzo (€)</span>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Input className={inputCls} placeholder="Min" inputMode="numeric" value={filters.prezzoMin} onChange={(e) => set({ prezzoMin: e.target.value.replace(/\D/g, "") })} data-testid="filter-prezzo-min" />
          <Input className={inputCls} placeholder="Max" inputMode="numeric" value={filters.prezzoMax} onChange={(e) => set({ prezzoMax: e.target.value.replace(/\D/g, "") })} data-testid="filter-prezzo-max" />
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-semibold text-anthracite/60">Superficie minima (mq)</span>
        <Input className={`${inputCls} mt-1`} placeholder="Es. 80" inputMode="numeric" value={filters.superficieMin} onChange={(e) => set({ superficieMin: e.target.value.replace(/\D/g, "") })} data-testid="filter-superficie" />
      </label>

      <div className="grid grid-cols-3 gap-3">
        <MinSelect label="Locali" value={filters.locali} onChange={(v) => set({ locali: v })} testid="filter-locali" />
        <MinSelect label="Camere" value={filters.camere} onChange={(v) => set({ camere: v })} testid="filter-camere" />
        <MinSelect label="Bagni" value={filters.bagni} onChange={(v) => set({ bagni: v })} testid="filter-bagni" />
      </div>

      <div>
        <span className="text-xs font-semibold text-anthracite/60">Caratteristiche</span>
        <div className="flex flex-wrap gap-2 mt-2">
          <Toggle label="Box / Posto auto" active={filters.box} onClick={() => set({ box: !filters.box })} testid="filter-box" />
          <Toggle label="Ascensore" active={filters.ascensore} onClick={() => set({ ascensore: !filters.ascensore })} testid="filter-ascensore" />
          <Toggle label="Balcone / Terrazzo / Giardino" active={filters.esterno} onClick={() => set({ esterno: !filters.esterno })} testid="filter-esterno" />
          <Toggle label="Nuova costruzione" active={filters.nuovaCostruzione} onClick={() => set({ nuovaCostruzione: !filters.nuovaCostruzione })} testid="filter-nuova" />
          <Toggle label="Da ristrutturare" active={filters.daRistrutturare} onClick={() => set({ daRistrutturare: !filters.daRistrutturare })} testid="filter-ristrutturare" />
        </div>
      </div>

      <div className="rounded-xl bg-accent-subtle text-brand text-sm font-semibold text-center py-3" data-testid="filters-count">
        {resultCount} immobili trovati
      </div>
    </div>
  );
}
